import React, { useEffect, useState } from "react";
import { api, formatApiError, coverUrl } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import { Radio, ChevronDown, ChevronUp } from "lucide-react";
import NowPlayingStage from "../components/NowPlayingStage";
import { recentlyPlayedIds } from "../lib/recentlyPlayed";

const DEFAULT_COVER = "https://images.unsplash.com/photo-1771747131849-41056d3d9be3?crop=entropy&cs=srgb&fm=jpg&w=800&q=85";

function RadioEmptyHero({ rotationCount, queue, onTuneIn, busy }) {
  return (
    <div
      data-testid="radio-hero-empty"
      className="card-surface relative overflow-hidden bg-gradient-to-br from-[#1f1a14] via-[#0E1014] to-[#0a0a14] p-6 md:p-10 min-h-[360px]"
    >
      <img src={DEFAULT_COVER} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-tr from-[#060709] via-[#060709]/70 to-transparent" />

      <div className="relative flex flex-col md:flex-row items-center md:items-stretch gap-6 md:gap-8">
        <div className="w-40 h-40 md:w-56 md:h-56 rounded-2xl border border-[#C5A059]/30 bg-[#0E1014] flex items-center justify-center flex-shrink-0 shadow-[0_0_60px_rgba(197,160,89,0.2)]">
          <Radio className="w-16 h-16 md:w-20 md:h-20 text-[#C5A059]/70" strokeWidth={1.2} />
        </div>
        <div className="flex-1 flex flex-col justify-center text-center md:text-left">
          <div className="overline text-[#C5A059] mb-2 flex items-center gap-2 justify-center md:justify-start">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C5A059] opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#C5A059]" />
            </span>
            On air · 24/7
          </div>
          <h1 className="font-serif text-[#FDFBF7] text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-none">
            Yah-Wave Radio <span className="italic text-[#C5A059]">Live</span>
          </h1>
          <p className="text-[#A1A4AB] max-w-xl mt-4 mx-auto md:mx-0 text-sm md:text-base">
            A continuous rotation of righteous music and teachings. Tune in and let the station play.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 items-center justify-center md:justify-start">
            <button
              onClick={onTuneIn}
              disabled={busy || queue.length === 0}
              className="btn-primary"
              data-testid="radio-tune-in"
            >
              <Radio className="w-5 h-5" strokeWidth={1.5} /> Tune in
            </button>
            <div className="text-sm text-[#A1A4AB]">
              {rotationCount} track{rotationCount !== 1 ? "s" : ""} on rotation
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RadioPage() {
  const [rotation, setRotation] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rotationOpen, setRotationOpen] = useState(true);
  const { playQueue, mode, currentTrack, needsTapToStart } = usePlayer();

  useEffect(() => {
    (async () => {
      try {
        const exclude = recentlyPlayedIds().join(",");
        const [rot, q] = await Promise.all([
          api.get("/radio/rotation"),
          api.get("/radio/queue", { params: { count: 60, ...(exclude ? { exclude } : {}) } }),
        ]);
        setRotation(rot.data || []);
        setQueue(q.data || []);
      } catch (e) {
        setErr(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const tuneIn = () => {
    if (queue.length === 0) return;
    playQueue(queue, 0, "radio");
  };

  const isLive = mode === "radio" && !!currentTrack;

  return (
    <div className="space-y-10">
      <NowPlayingStage
        isActive={isLive}
        emptyState={
          <RadioEmptyHero
            rotationCount={rotation.length}
            queue={queue}
            onTuneIn={tuneIn}
            busy={loading}
          />
        }
        testIdPrefix="radio-hero"
        onAirLabel="On Air · Yah-Wave Live"
        livePillLabels={{ playing: "ON AIR", paused: "PAUSED" }}
        extraInfoBelow={
          needsTapToStart ? (
            <div className="mt-3 overline text-xs text-[#C5A059]">Tap play to start radio</div>
          ) : null
        }
      />

      {err && <div className="text-red-400 text-sm" data-testid="radio-error">{err}</div>}

      <section data-testid="rotation-section" className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="overline text-[#C5A059] mb-1">The lineup</div>
            <h2 className="font-serif text-2xl text-[#FDFBF7] tracking-tight">In the rotation</h2>
          </div>
          <button
            type="button"
            onClick={() => setRotationOpen((v) => !v)}
            className="text-xs overline px-3 py-1.5 rounded-full border border-[#22262E] text-[#A1A4AB] hover:border-[#C5A059]/40 hover:text-[#FDFBF7] inline-flex items-center gap-1.5"
            data-testid="rotation-toggle"
          >
            {rotationOpen
              ? (<><ChevronUp className="w-3.5 h-3.5" strokeWidth={1.5} /> Hide</>)
              : (<><ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} /> {rotation.length} track{rotation.length !== 1 ? "s" : ""}</>)}
          </button>
        </div>

        {rotationOpen && (
          loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-16 shimmer rounded-md" />)}
            </div>
          ) : rotation.length === 0 ? (
            <div className="card-surface p-10 text-center">
              <div className="text-[#FDFBF7] font-serif text-xl mb-1">No tracks in rotation yet</div>
              <div className="text-[#A1A4AB] text-sm">Admins add approved tracks to the live rotation from the admin dashboard.</div>
            </div>
          ) : (
            <div className="card-surface divide-y divide-[#22262E]/60" data-testid="rotation-list">
              {rotation.map((t, i) => (
                <button
                  key={t.id}
                  onClick={() => playQueue(rotation, i, "radio")}
                  className="w-full flex items-center gap-4 p-3 hover:bg-white/[0.02] transition-colors text-left"
                  data-testid={`rotation-row-${t.id}`}
                >
                  <div className="w-12 h-12 rounded-md overflow-hidden border border-[#22262E] bg-[#111317] flex-shrink-0">
                    <img src={coverUrl(t) || DEFAULT_COVER} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[#FDFBF7] truncate">{t.title}</div>
                    <div className="text-[#A1A4AB] text-sm truncate">{t.artist}</div>
                  </div>
                  <div className="overline text-[#6C707A] hidden md:block">{t.category}</div>
                </button>
              ))}
            </div>
          )
        )}
      </section>
    </div>
  );
}
